/**
 * OrganizerRenderer - Renders organizer elements (orange section wrapper)
 * Implements orange bordered sections with title box above
 */

import Konva from 'konva';
import { BaseRenderer } from '../core/BaseRenderer';
import { EditableElement } from '../editing/types';

export class OrganizerRenderer extends BaseRenderer {
  private borderRect: Konva.Rect | null = null;
  private titleBoxRect: Konva.Rect | null = null;
  private titleText: Konva.Text | null = null;

  constructor(element: EditableElement) {
    super(element);
  }

  /**
   * Render the organizer element
   */
  render(): Konva.Node {
    const { width, height, data } = this.element;
    
    // Main border rectangle
    this.borderRect = new Konva.Rect({
      width: width,
      height: height,
      fill: '#FFFBF0',
      stroke: '#FFC470',
      strokeWidth: 2,
      cornerRadius: 8,
    });
    
    // Title box above section
    const titleBoxWidth = Math.min(250, width);
    this.titleBoxRect = new Konva.Rect({
      y: -48,
      width: titleBoxWidth,
      height: 38,
      fill: '#FFFBF0',
      stroke: '#FFC470',
      strokeWidth: 2,
      cornerRadius: 8,
    });
    
    // Title text
    this.titleText = new Konva.Text({
      x: 12,
      y: -36,
      text: data?.title || '',
      fontSize: 15,
      fontFamily: 'Inter, sans-serif',
      fontStyle: 'bold',
      fill: '#000000',
      letterSpacing: 0.3,
    });
    
    this._node.add(this.borderRect);
    this._node.add(this.titleBoxRect);
    this._node.add(this.titleText);
    
    return this._node;
  }

  /**
   * Update the organizer with new properties
   */
  update(updates: Partial<EditableElement>): void {
    this.updateElementData(updates);
    
    const { width, height, data } = this.element;
    
    // Update border
    if (this.borderRect) {
      if (updates.width !== undefined || updates.height !== undefined) {
        this.borderRect.width(width);
        this.borderRect.height(height);
      }
    }
    
    // Update title box
    if (this.titleBoxRect && updates.width !== undefined) {
      const titleBoxWidth = Math.min(250, width);
      this.titleBoxRect.width(titleBoxWidth);
    }
    
    // Update title text
    if (this.titleText && updates.data?.title !== undefined) {
      this.titleText.text(data?.title || '');
    }
  }

  /**
   * Set dimensions and update internal nodes
   */
  setDimensions(width: number, height: number): void {
    super.setDimensions(width, height);
    
    if (this.borderRect) {
      this.borderRect.width(width);
      this.borderRect.height(height);
    }
    
    if (this.titleBoxRect) {
      const titleBoxWidth = Math.min(250, width);
      this.titleBoxRect.width(titleBoxWidth);
    }
  }

  /**
   * Get title
   */
  getTitle(): string {
    return this.element.data?.title || '';
  }

  /**
   * Set title
   */
  setTitle(title: string): void {
    if (this.titleText) {
      this.titleText.text(title);
    }
    if (this.element.data) {
      this.element.data.title = title;
    } else {
      this.element.data = { title };
    }
  }

  /**
   * Destroy the renderer
   */
  destroy(): void {
    this.borderRect = null;
    this.titleBoxRect = null;
    this.titleText = null;
    super.destroy();
  }
}
